/**
 * Whale Tracker — Gated Premium Feature
 * Track smart money movements across DeFi
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users, TrendingUp, RefreshCw, Unlock,
  ExternalLink, DollarSign, Activity, Filter, X,
  ChevronRight, BarChart2, Layers, Brain, Lock
} from 'lucide-react';
import UpgradeTeaser from '../components/UpgradeTeaser';
import { FeatureInfo } from '../components/FeatureInfo';
import { getBackendBaseUrl } from '../lib/apiBase';

const API_URL = getBackendBaseUrl();

// Robust USD formatter — handles any size without scientific notation
const formatUSD = (v) => {
  if (!v || isNaN(v) || !isFinite(v) || v <= 0) return null;
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9)  return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6)  return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3)  return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
};
// Sample data shown as blurred teaser for non-Pro users
const SAMPLE_WHALE_PREVIEW = [
  { label: 'Jump Trading', type: 'Market Maker', activity: 'Deploying', detail: '$28M into Curve, Convex', portfolio_usd: 28000000, top_protocols: ['Curve', 'Convex', 'Frax'] },
  { label: 'Wintermute', type: 'Market Maker', activity: 'Accumulating', detail: '$19M into Uniswap V3, GMX', portfolio_usd: 19000000, top_protocols: ['Uniswap V3', 'GMX'] },
  { label: 'Cumberland DRW', type: 'Market Maker', activity: 'Deploying', detail: '$11M into Balancer, Aave V3', portfolio_usd: 11000000, top_protocols: ['Balancer', 'Aave V3'] },
];
const WhaleDetailModal = ({ whale, onClose, canUseAI, token }) => {
  const [aiInsight, setAIInsight] = useState(null);
  const [aiLoading, setAILoading] = useState(false);
  if (!whale) return null;
  const activityColor = whale.activity === 'Deploying' ? '#00ff88' : whale.activity === 'Accumulating' ? '#00d4ff' : '#ffa502';

  const fetchAIInsight = async () => {
    if (!canUseAI) { toast.info('AI Whale Insights require Elite ($79/mo)'); return; }
    setAILoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/whale-insight`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify({
          label: whale.label,
          address: whale.full_address || whale.address,
          whale_type: whale.type,
          top_protocols: whale.top_protocols || [],
          top_tokens: whale.top_tokens || [],
          portfolio_usd: whale.portfolio_usd,
          activity: whale.activity,
          detail: whale.detail,
          chain: whale.chain,
        }),
      });
      const data = await res.json();
      if (data.gated) { toast.info(data.message); }
      else if (data.insight) { setAIInsight(data.insight); }
    } catch { toast.error('AI insight unavailable — try again shortly'); }
    setAILoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={e => e.target === e.currentTarget && onClose()} data-testid="whale-detail-modal">
      <div className="bg-[#1a1a2e] max-w-md w-full rounded-xl border border-gray-800 overflow-hidden my-auto">
        {/* Header — sticky so close button always reachable */}
        <div className="sticky top-0 z-10 bg-[#1a1a2e] p-4 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#00d4ff]/10 flex items-center justify-center text-lg font-bold text-white">
              {whale.label?.[0] || '?'}
            </div>
            <div>
              <h3 className="font-bold text-white">{whale.label}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-gray-500 font-mono">{whale.full_address ? `${whale.full_address.slice(0,8)}...${whale.full_address.slice(-6)}` : whale.address}</span>
                <span className="text-[10px] px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded">{whale.type}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white transition-colors" data-testid="close-whale-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Activity & Portfolio */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#0a0a12] rounded-xl p-3 border border-gray-800">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Activity</p>
              <p className="text-base font-bold" style={{ color: activityColor }}>{whale.activity}</p>
              <p className="text-xs text-gray-400 mt-0.5 leading-tight">{whale.detail}</p>
            </div>
            <div className="bg-[#0a0a12] rounded-xl p-3 border border-gray-800">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Portfolio Size</p>
              {formatUSD(whale.portfolio_usd) ? (
                <p className="text-base font-bold text-[#00ff88]">{formatUSD(whale.portfolio_usd)}</p>
              ) : (
                <p className="text-sm text-gray-500 italic">Tracking activity only</p>
              )}
              <p className="text-[10px] text-gray-500 mt-0.5">Chain: {whale.chain}</p>
            </div>
          </div>

          {/* Top Protocols */}
          {whale.top_protocols?.length > 0 && (
            <div className="bg-[#0a0a12] rounded-xl p-3 border border-gray-800">
              <div className="flex items-center gap-2 mb-2.5">
                <Layers className="w-3.5 h-3.5 text-[#00ff88]" />
                <p className="text-xs text-gray-400 uppercase tracking-wider">Active In</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {whale.top_protocols.map((p, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 bg-[#00ff88]/10 text-[#00ff88] rounded-lg font-medium">{p}</span>
                ))}
              </div>
            </div>
          )}

          {/* Top Tokens */}
          {whale.top_tokens?.length > 0 && (
            <div className="bg-[#0a0a12] rounded-xl p-3 border border-gray-800">
              <div className="flex items-center gap-2 mb-2.5">
                <DollarSign className="w-3.5 h-3.5 text-[#ffa502]" />
                <p className="text-xs text-gray-400 uppercase tracking-wider">Top Holdings</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {whale.top_tokens.map((t, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 bg-[#ffa502]/10 text-[#ffa502] rounded-lg font-medium">{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* AI Whale Insight */}
          <div className="bg-[#00d4ff]/5 border border-[#00d4ff]/20 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Brain className="w-3.5 h-3.5 text-[#00d4ff]" />
                <p className="text-xs font-bold text-[#00d4ff]">AI Whale Intelligence</p>
                <FeatureInfo featureKey="whale_ai_insight" size="xs" />
              </div>
              {canUseAI ? (
                <button
                  onClick={fetchAIInsight}
                  disabled={aiLoading}
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-[#00d4ff]/15 text-[#00d4ff] border border-[#00d4ff]/30 rounded-lg text-[10px] font-bold hover:bg-[#00d4ff]/25 transition-all disabled:opacity-50"
                  data-testid={`whale-ai-btn-${whale.label?.replace(/\s/g, '-').toLowerCase()}`}
                >
                  {aiLoading ? <><RefreshCw className="w-3 h-3 animate-spin" /> Analyzing...</> : <><Brain className="w-3 h-3" /> {aiInsight ? 'Refresh' : 'Get Insight'}</>}
                </button>
              ) : (
                <span className="flex items-center gap-1 text-[10px] text-gray-600 bg-gray-800/50 px-2 py-0.5 rounded border border-gray-700">
                  <Lock className="w-3 h-3" /> Elite
                </span>
              )}
            </div>
            {!aiInsight && !aiLoading && (
              <p className="text-[10px] text-gray-600 leading-relaxed">
                {canUseAI
                  ? 'Click "Get Insight" for AI analysis of this wallet\'s strategy and what it means for your investments.'
                  : 'Upgrade to Elite ($79/mo) to unlock AI-powered whale strategy analysis.'}
              </p>
            )}
            {aiLoading && (
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                <RefreshCw className="w-3 h-3 animate-spin" /> Analyzing {whale.label}\'s DeFi strategy...
              </div>
            )}
            {aiInsight && (
              <div className="space-y-2.5 mt-1" data-testid="whale-ai-insight-result">
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Strategy</p>
                  <p className="text-xs text-gray-300 leading-relaxed">{aiInsight.strategy}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Market Signal</p>
                  <p className="text-xs text-[#00d4ff] leading-relaxed">{aiInsight.signal}</p>
                </div>
                {aiInsight.key_observations?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Key Observations</p>
                    <ul className="space-y-1">
                      {aiInsight.key_observations.map((obs, i) => (
                        <li key={i} className="text-[10px] text-gray-400 leading-relaxed flex gap-1.5">
                          <span className="text-[#00d4ff] flex-shrink-0">•</span>{obs}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="bg-[#00ff88]/8 border border-[#00ff88]/15 rounded-lg p-2">
                  <p className="text-[10px] text-[#00ff88]/80 uppercase tracking-wide mb-0.5">For You</p>
                  <p className="text-xs text-gray-300 leading-relaxed">{aiInsight.retail_takeaway}</p>
                </div>
              </div>
            )}
          </div>

          {/* What This Means */}
          <div className="bg-[#00ff88]/5 border border-[#00ff88]/15 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-1 font-medium">What This Signal Means</p>
            <p className="text-xs text-gray-300 leading-relaxed">
              {whale.activity === 'Deploying'
                ? `${whale.label} is actively deploying capital into DeFi protocols. This is a bullish signal for the protocols they're entering — smart money is putting funds to work.`
                : whale.activity === 'Accumulating'
                  ? `${whale.label} is building token positions without deploying to DeFi yet. This suggests preparation for a larger move or waiting for better entry conditions.`
                  : `${whale.label} is not making significant moves right now. Monitor for upcoming changes in their allocation strategy.`
              }
            </p>
          </div>

          {/* Etherscan Link */}
          <a
            href={`https://etherscan.io/address/${whale.full_address || whale.address}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg text-sm transition-colors"
            data-testid="whale-etherscan-link"
          >
            <ExternalLink className="w-4 h-4" />
            View on Etherscan
          </a>

          {whale.source === 'moralis' && (
            <p className="text-[9px] text-gray-700 text-center">Data via Moralis Web3 API • Refreshed every 30 min</p>
          )}
          {whale.source === 'mock' && (
            <p className="text-[9px] text-[#ffa502]/50 text-center">Preview data — connect Moralis API key for live tracking</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Whale Card ───────────────────────────────────────────────
const WhaleCard = ({ whale, onClick }) => {
  const activityColor = whale.activity === 'Deploying' ? '#00ff88' : whale.activity === 'Accumulating' ? '#00d4ff' : '#ffa502';
  return (
    <div
      className="neon-card p-4 rounded-xl hover:border-[#00ff88]/20 transition-all cursor-pointer group"
      onClick={() => onClick(whale)}
      data-testid={`whale-card-${whale.label?.replace(/\s/g, '-').toLowerCase()}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-white text-sm truncate">{whale.label}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-gray-500 font-mono">{whale.address}</span>
            <span className="text-[10px] px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded flex-shrink-0">{whale.type}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <span className="text-xs font-bold px-2 py-1 rounded" style={{ color: activityColor, backgroundColor: `${activityColor}15` }}>
            {whale.activity}
          </span>
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-3 leading-relaxed">{whale.detail}</p>

      <div className="grid grid-cols-2 gap-3 text-xs mb-3">
        <div className="min-w-0">
          <p className="text-gray-500 mb-0.5">Portfolio</p>
          <p className="text-[#00ff88] font-bold truncate">{formatUSD(whale.portfolio_usd) || 'No data available'}</p>
        </div>
        <div>
          <p className="text-gray-500 mb-0.5">Chain</p>
          <p className="text-white font-medium">{whale.chain}</p>
        </div>
      </div>

      {whale.top_protocols?.length > 0 && (
        <div className="border-t border-gray-800 pt-2">
          <p className="text-[10px] text-gray-600 mb-1.5">Active In</p>
          <div className="flex flex-wrap gap-1">
            {whale.top_protocols.map((p, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 bg-[#00ff88]/10 text-[#00ff88] rounded">{p}</span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-800/60">
        {whale.source === 'moralis'
          ? <p className="text-[9px] text-gray-700">via Moralis API</p>
          : <p className="text-[9px] text-[#ffa502]/50">preview data</p>
        }
        <div className="flex items-center gap-1 text-[10px] text-gray-600 group-hover:text-gray-400 transition-colors">
          <span>View details</span>
          <ChevronRight className="w-3 h-3" />
        </div>
      </div>
    </div>
  );
};

// ─── Main Whale Tracker Component ────────────────────────────
export default function WhaleTracker({ onAuthClick, onUpgrade }) {
  const { user, token, isAuthenticated } = useAuth();
  const [whaleData, setWhaleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedWhale, setSelectedWhale] = useState(null);

  const userTier = user?.subscription?.tier || 'free';
  const isAdmin = user?.role === 'admin';
  const isPremium = userTier === 'elite' || userTier === 'unlimited' || isAdmin;

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/yield-intelligence/whales`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) setWhaleData(await res.json());
      } catch (e) {
        console.error('Whale fetch error:', e);
      }
      setLoading(false);
    })();
  }, [token]);

  const handleUpgrade = () => {
    if (onUpgrade) onUpgrade();
    else toast.info('Redirecting to subscription...');
  };

  // Sort whales by portfolio value descending for consistent display across sessions
  const whales = [...(whaleData?.whales || [])].sort((a, b) => (b.portfolio_usd || 0) - (a.portfolio_usd || 0));
  const filteredWhales = whales.filter(w => filter === 'all' || w.chain?.toLowerCase() === filter);
  const chains = [...new Set(whales.map(w => w.chain?.toLowerCase()).filter(Boolean))];

  // Safe total — cap individual values to prevent overflow display
  const safeTotal = whales.reduce((s, w) => {
    const v = w.portfolio_usd || 0;
    return s + (isFinite(v) && v < 1e12 ? v : 0);
  }, 0);

  return (
    <div className="min-h-screen bg-[#0a0a12] p-4 md:p-6 relative" data-testid="whale-tracker">
      {/* Detail Modal */}
      {selectedWhale && <WhaleDetailModal whale={selectedWhale} onClose={() => setSelectedWhale(null)} canUseAI={isPremium} token={token} />}

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#00d4ff]/10 rounded-lg">
              <span className="text-2xl">🐳</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-white">Whale Tracker</h1>
                <FeatureInfo featureKey="whale_tracker" />
              </div>
              <p className="text-sm text-gray-400">Real-time smart money movements</p>
            </div>
          </div>
          {isPremium && (
            <button
              onClick={() => { setLoading(true); window.location.reload(); }}
              className="flex items-center gap-2 px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm">Refresh</span>
            </button>
          )}
        </div>

        <div className="relative">
          {!isPremium ? (
            <UpgradeTeaser
              tier="elite"
              feature="Whale Intelligence — 10 Wallets Tracked"
              description="See where $1M+ institutional wallets are deploying capital in real-time. Jump Trading, Wintermute, Cumberland DRW, Coinbase Custody and more."
              onSignIn={onAuthClick}
              blurAmount="sm"
              className="min-h-[580px]"
            >
              {/* Preview teaser: fake stats + sample whale cards */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Tracked Whales', value: '10', color: 'text-white' },
                    { label: 'Total Portfolio', value: '$178.4M', color: 'text-[#00ff88]' },
                    { label: 'Data Source', value: 'Moralis', color: 'text-[#00d4ff]' },
                    { label: 'Powered By', value: 'Nighton+Balinas', color: 'text-[#ffa502]' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="neon-card p-4 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">{label}</p>
                      <p className={`text-xl font-bold truncate ${color}`}>{value}</p>
                    </div>
                  ))}
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {SAMPLE_WHALE_PREVIEW.map((whale, i) => (
                    <div key={i} className="neon-card rounded-xl p-4 border border-gray-800">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#00d4ff]/10 flex items-center justify-center text-sm font-bold text-white">{whale.label[0]}</div>
                          <div><p className="text-sm font-bold text-white truncate">{whale.label}</p><p className="text-[10px] text-gray-500">{whale.type}</p></div>
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] font-bold">{whale.activity}</span>
                      </div>
                      <p className="text-xs text-gray-400 mb-2">{whale.detail}</p>
                      <div className="flex flex-wrap gap-1">
                        {whale.top_protocols.map(p => <span key={p} className="text-[10px] px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded">{p}</span>)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </UpgradeTeaser>
          ) : (
            <>
              {/* Stats Bar */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="neon-card p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Tracked Whales</p>
                  <p className="text-2xl font-bold text-white" data-testid="whale-count">{whaleData?.total_tracked || whales.length || 10}</p>
                </div>
                <div className="neon-card p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Total Portfolio</p>
                  <p className="text-2xl font-bold text-[#00ff88] truncate" data-testid="whale-total-portfolio">{formatUSD(safeTotal || 178400000)}</p>
                </div>
                <div className="neon-card p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Data Source</p>
                  <p className="text-sm font-bold text-[#00d4ff]">{whaleData?.data_source || 'Moralis'}</p>
                </div>
                <div className="neon-card p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Powered By</p>
                  <p className="text-sm font-bold text-[#ffa502]">{whaleData?.powered_by || 'Nighton + Balinas'}</p>
                </div>
              </div>

              {/* Filter Bar */}
              <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
                <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${filter === 'all' ? 'bg-[#00ff88]/20 text-[#00ff88]' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
                >
                  All Chains
                </button>
                {chains.map(chain => (
                  <button key={chain} onClick={() => setFilter(chain)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors capitalize ${filter === chain ? 'bg-[#00ff88]/20 text-[#00ff88]' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
                    {chain}
                  </button>
                ))}
              </div>

              {/* Whale Grid */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                  <RefreshCw className="w-8 h-8 animate-spin text-[#00ff88]" />
                  <p className="text-sm text-gray-500">Fetching whale positions via Moralis...</p>
                  <p className="text-xs text-gray-600">This may take up to 15 seconds on first load</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredWhales.map((whale, i) => (
                    <WhaleCard key={i} whale={whale} onClick={setSelectedWhale} />
                  ))}
                  {filteredWhales.length === 0 && (
                    <div className="col-span-3 text-center py-12 text-gray-500">
                      No whale data available for this chain.
                    </div>
                  )}
                </div>
              )}

              <p className="text-center text-xs text-gray-600 mt-6">
                {whaleData?.note || 'Whale positions refreshed every 30 minutes via Moralis Web3 API'} • Click any card for full details
              </p>
              <p className="text-center text-[10px] text-gray-700 mt-1.5" data-testid="whale-portfolio-disclaimer">
                Portfolio values reflect tracked LP &amp; DeFi positions only — not full on-chain holdings or CEX balances
              </p>
            </>
          )}

          {/* Disclaimer shown to all users */}
          {!isPremium && (
            <p className="text-center text-[10px] text-gray-700 mt-4" data-testid="whale-portfolio-disclaimer">
              Portfolio values reflect tracked LP &amp; DeFi positions only — not full on-chain holdings or CEX balances
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
